import { useMemo, useEffect } from "react"; // Added useEffect for dev warning
import { useTranslation } from "react-i18next";

interface PaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  // Option 1: Finite pagination
  totalPages?: number;
  // Option 2: Infinite/Unknown total pagination
  hasNextPage?: boolean;
  maxVisiblePages?: number;
  className?: string;
  buttonClassName?: string;
  activeButtonClassName?: string;
  disabledButtonClassName?: string;
}

export default function Pagination({
  currentPage,
  totalPages,
  hasNextPage,
  onPageChange,
  maxVisiblePages = 6,
  className = "flex justify-center items-center mt-6 gap-2",
  buttonClassName = "px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700",
  activeButtonClassName = "!bg-primary-400 dark:!bg-primary-500 text-white",
  disabledButtonClassName = "disabled:opacity-50",
}: PaginationProps) {
  const { t } = useTranslation();

  // Determine if we are in finite pagination mode
  const isFinitePagination = typeof totalPages === "number" && totalPages > 0;

  const visiblePageNumbers = useMemo(() => {
    // Only calculate if in finite mode and totalPages is valid
    if (!isFinitePagination || !totalPages) {
      // Added !totalPages for type safety within this block
      return [];
    }

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = 1;

    if (currentPage > halfVisible) {
      startPage = Math.min(
        currentPage - halfVisible,
        totalPages - maxVisiblePages + 1,
      );
    }
    startPage = Math.max(1, startPage);

    return Array.from(
      { length: Math.min(maxVisiblePages, totalPages) },
      (_, i) => startPage + i,
    ).filter((page) => page <= totalPages);
  }, [currentPage, totalPages, maxVisiblePages, isFinitePagination]);

  // Condition to render nothing
  if (isFinitePagination) {
    if (totalPages! <= 1) {
      // Use ! because totalPages is confirmed number > 0 here by isFinitePagination
      return null;
    }
  } else {
    // In "unknown total" mode, if we are on page 1 and there's no next page,
    // then there's effectively only one page known, so no pagination controls needed.
    if (currentPage === 1 && hasNextPage === false) {
      return null;
    }
    // If hasNextPage is not provided, the "Next" button might behave unpredictably disabled
    // or always enabled. The dev warning above addresses this.
  }

  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = isFinitePagination
    ? currentPage === totalPages
    : hasNextPage === false; // If hasNextPage is undefined, next is not disabled.

  return (
    <div className={className}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isPreviousDisabled}
        className={`${buttonClassName} ${disabledButtonClassName}`}
      >
        {t("common.previous")}
      </button>

      {/* Only show page numbers if in finite pagination mode */}
      {isFinitePagination && visiblePageNumbers.length > 0 && (
        <div className="hidden sm:flex gap-1">
          {visiblePageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`${buttonClassName} ${
                currentPage === page ? activeButtonClassName : ""
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/*
        If not in finite pagination mode, we might want to display the current page
        as a simple indicator, even without other numbers.
        Example (optional, uncomment and style if desired):
      */}
      {!isFinitePagination && (
        <span className={`${buttonClassName} cursor-default`}>
          {t("common.page")} {currentPage}
        </span>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isNextDisabled}
        className={`${buttonClassName} ${disabledButtonClassName}`}
      >
        {t("common.next")}
      </button>
    </div>
  );
}
